import axios from 'axios'
import {BASE_URL} from '@/utils/constants'

/**
 * 认证令牌（access token / refresh token）统一管理
 * 对应后端安全加固:access token 2h、refresh token 7d、刷新旋转、登出吊销
 */

// localStorage 键名。token 存 'Bearer xxx' 形式,兼容全站现有取值方式
const TOKEN_KEY = 'token'
const ACCESS_TOKEN_KEY = 'accessToken'
const REFRESH_TOKEN_KEY = 'refreshToken'
const TOKEN_TIME_KEY = 'tokentime'
const ACCESS_EXPIRE_AT_KEY = 'accessTokenExpireAt'

// 临界提前量,避免请求在飞行途中过期
const EXPIRE_AHEAD_MS = 30 * 1000

// 这些地址自身处理认证逻辑,响应失败时不再触发刷新
const SKIP_REFRESH_URLS = [
    'auth/refreshtoken',
    'auth/logout',
    'managers/login',
    'readers/login',
    'publicKey'
]

// HTTP 200 但 ok:false 时,用于识别令牌失效消息(收紧范围,避免误伤业务提示)
const INVALID_TOKEN_RE = /令牌|token|未登录|登录已失效|请重新登录|身份认证/i

// 进行中的刷新请求(单飞,防止并发请求各自刷新导致刷新旋转互相顶掉)
let refreshingPromise = null
// 已触发跳转登录页的标记
let redirecting = false

/**
 * 登录/刷新成功后保存令牌
 * @param {{token?:string, accessToken?:string, refreshToken?:string, expiresIn?:number}} data
 */
export function setTokens(data) {
    const accessToken = data.accessToken || data.token
    if (!accessToken) return
    localStorage.setItem(TOKEN_KEY, 'Bearer ' + accessToken)
    localStorage.setItem(ACCESS_TOKEN_KEY, accessToken)
    if (data.refreshToken) {
        localStorage.setItem(REFRESH_TOKEN_KEY, data.refreshToken)
    }
    localStorage.setItem(TOKEN_TIME_KEY, String(Date.now()))
    const expiresIn = data.expiresIn || 7200
    localStorage.setItem(ACCESS_EXPIRE_AT_KEY, String(Date.now() + expiresIn * 1000))
    redirecting = false
}

/**
 * 解码 JWT payload(不校验签名,仅用于读取 Expire 等 claim)
 * @param {string} token
 * @returns {object|null}
 */
export function decodeJwtPayload(token) {
    try {
        if (!token || token.split('.').length !== 3) return null
        const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/')
        const json = decodeURIComponent(
            atob(base64).split('').map(c =>
                '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
            ).join('')
        )
        return JSON.parse(json)
    } catch (e) {
        return null
    }
}

function getTokenExpire(token) {
    const payload = decodeJwtPayload(token)
    return payload ? (payload.Expire || payload.exp) : null
}

/**
 * access token 是否已过期(提前 EXPIRE_AHEAD_MS 视为过期)
   */
export function isAccessTokenExpired() {
    const expireAt = Number(localStorage.getItem(ACCESS_EXPIRE_AT_KEY))
    if (expireAt && !Number.isNaN(expireAt)) {
        return Date.now() >= expireAt - EXPIRE_AHEAD_MS
    }
    // 兜底:直接解 JWT 的 Expire claim
    const exp = getTokenExpire(localStorage.getItem(ACCESS_TOKEN_KEY))
    return !exp || Date.now() >= exp * 1000 - EXPIRE_AHEAD_MS
}

/**
 * refresh token 是否已过期
 */
export function isRefreshTokenExpired() {
    const exp = getTokenExpire(localStorage.getItem(REFRESH_TOKEN_KEY))
    return !exp || Date.now() >= exp * 1000
}

/**
 * 用 refresh token 换取新的 access/refresh token(刷新旋转)。
 * 用裸 axios 调用,不走被拦截的实例,避免循环刷新。
 * @returns {Promise<string>} resolve 出新的 'Bearer xxx'
 */
export function refreshAccessToken() {
    if (refreshingPromise) return refreshingPromise
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (!refreshToken) {
        return Promise.reject(new Error('refreshToken不能为空'))
    }
    if (isRefreshTokenExpired()) {
        return Promise.reject(new Error('刷新令牌已过期,请重新登录'))
    }
    refreshingPromise = axios({
        url: BASE_URL + 'auth/refreshtoken',
        method: 'post',
        data: JSON.stringify({refreshToken}),
        headers: {'Content-Type': 'application/json'}
    }).then(res => {
        if (res.data && res.data.ok) {
            setTokens(res.data)
            return localStorage.getItem(TOKEN_KEY)
        }
        throw new Error((res.data && res.data.msg) || '刷新令牌无效或已过期')
    }).finally(() => {
        refreshingPromise = null
    })
    return refreshingPromise
}

/**
 * 路由守卫用:保证有一个可用的 access token,过期则静默刷新
 * @returns {Promise<boolean>}
 */
export async function ensureAccessToken() {
    if (!localStorage.getItem(TOKEN_KEY)) return false
    if (!isAccessTokenExpired()) return true
    if (isRefreshTokenExpired()) return false
    try {
        await refreshAccessToken()
        return true
    } catch (e) {
        return false
    }
}

/**
 * 清理本地会话并跳转登录页(只触发一次)
 */
export function clearAuthAndRedirect() {
    localStorage.clear()
    sessionStorage.clear()
    if (redirecting) return
    redirecting = true
    if (!location.hash.startsWith('#/login')) {
        location.hash = '#/login'
    }
}

/**
 * 主动登出:通知后端吊销令牌(access 必吊销,refresh 合法则一并吊销)。
 * 无论接口成功失败,都清理本地会话。
 * @returns {Promise<void>}
 */
export function logout() {
    const authHeader = localStorage.getItem(TOKEN_KEY)
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    const done = () => {
        localStorage.clear()
        sessionStorage.clear()
    }
    if (!authHeader) {
        done()
        return Promise.resolve()
    }
    return axios({
        url: BASE_URL + 'auth/logout',
        method: 'post',
        data: JSON.stringify(refreshToken ? {refreshToken} : {}),
        headers: {
            'Content-Type': 'application/json',
            'Authorization': authHeader
        }
    }).catch(() => {
        // 令牌可能已过期等,本地仍需清理
    }).then(done)
}

function isSkipUrl(url) {
    if (!url) return false
    return SKIP_REFRESH_URLS.some(u => url.indexOf(u) !== -1)
}

/**
 * 给 axios 实例(或全局 axios)挂载认证拦截器:
 * - 请求发出前若视图未手动带 Authorization,自动补当前 access token;
 * - 响应为 401 或 ok:false 的令牌失效消息时,刷新令牌并重放原请求;
 * - 刷新失败则清理会话回登录页。
 * @param {import('axios').AxiosInstance | typeof import('axios')} instance
 */
export function setupAuthInterceptors(instance) {
    // 刷新成功后用原实例重放,保证与原请求行为一致
    const retryWithRefresh = (config) => {
        return refreshAccessToken().then(newAuthHeader => {
            config.headers = config.headers || {}
            config.headers.Authorization = newAuthHeader
            return instance.request(config)
        }).catch(err => {
            clearAuthAndRedirect()
            return Promise.reject(err)
        })
    }

    instance.interceptors.request.use(config => {
        if (!config.headers.Authorization && !isSkipUrl(config.url) &&
            localStorage.getItem(TOKEN_KEY)) {
            config.headers.Authorization = localStorage.getItem(TOKEN_KEY)
        }
        return config
    })

    instance.interceptors.response.use(
        response => {
            const data = response.data
            const config = response.config
            if (data && data.ok === false && typeof data.msg === 'string' &&
                INVALID_TOKEN_RE.test(data.msg) && !isSkipUrl(config.url)) {
                return retryWithRefresh(config)
            }
            return response
        },
        error => {
            const config = error.config
            if (config && error.response && error.response.status === 401 &&
                !config._authRetried && !isSkipUrl(config.url)) {
                config._authRetried = true
                return retryWithRefresh(config)
            }
            return Promise.reject(error)
        }
    )
}
